import { useEffect, useState } from "react";
import { useParams, NavLink } from "react-router-dom";
import { getUser } from "../../http";
import { useSelector } from "react-redux";
import { format } from "date-fns";

const Employee = () => {
  const [user, setUser] = useState({
    name: "",
    email: "",
    mobile: "",
    image: "",
    address: "",
    status: "",
    username: "",
    type: "",
    team: "",
    total_leaves: 0,
    dob: "",
    age: 0,
    maritalStatus: "",
    family: {
      father: {},
      mother: {},
      spouse: {},
      siblings: [],
    },
    education: [],
    workExperience: [],
    totalExperience: "",
    currentCTC: "",
    expectedCTC: "",
    noticePeriod: "",
    willingToWorkShifts: "",
    references: [],
  });

  const { id } = useParams();
  const { user: loggedInUser } = useSelector((state) => state.authSlice);

  useEffect(() => {
    (async () => {
      const res = await getUser(id, loggedInUser.type);
      if (res.success) setUser(res.data);
    })();
    // eslint-disable-next-line
  }, [id]);

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "PPP");
    } catch (error) {
      return "Invalid date";
    }
  };

  return (
    <>
      <div className="main-content">
        <section className="section">
          <div className="section-header d-flex justify-content-between">
            <h1>Employee Profile</h1>
            <NavLink to={`/edituser/${id}`} className="btn btn-primary">
              Edit Profile
            </NavLink>
          </div>

          {/* Basic Information Card */}
          <div className="card mb-4">
            <div className="card-header">
              <h4>Basic Information</h4>
            </div>
            <div className="card-body row">
              <div className="col-md-3">
                <img
                  className="img-fluid img-thumbnail"
                  src={user.image}
                  alt={user.name}
                />
              </div>
              <div className="col-md-9">
                <table className="table">
                  <tbody>
                    <tr>
                      <th>Name</th>
                      <td>{user.name}</td>
                    </tr>
                    <tr>
                      <th>Username</th>
                      <td>{user.username}</td>
                    </tr>
                    <tr>
                      <th>Email</th>
                      <td>{user.email}</td>
                    </tr>
                    <tr>
                      <th>Mobile</th>
                      <td>{user.mobile}</td>
                    </tr>
                    <tr>
                      <th>Address</th>
                      <td>{user.address}</td>
                    </tr>
                    <tr>
                      <th>Role</th>
                      <td>{user.type}</td>
                    </tr>
                    <tr>
                      <th>Status</th>
                      <td>
                        <span
                          className={`badge ${
                            user.status === "active"
                              ? "badge-success"
                              : "badge-danger"
                          }`}>
                          {user.status}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <th>Team</th>
                      <td>{user.team?.name || "Not assigned"}</td>
                    </tr>
                    <tr>
                      <th>Total Leaves</th>
                      <td>{user.total_leaves}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Optional Information Section */}
          <div className="row">
            <div className="col-12">
              <div className="alert alert-info">
                The following sections contain optional information that may not
                be complete.
              </div>
            </div>
          </div>

          {/* Personal Information Card */}
          <div className="card mb-4">
            <div className="card-header">
              <h4>Personal Information (Optional)</h4>
            </div>
            <div className="card-body">
              <table className="table">
                <tbody>
                  <tr>
                    <th>Date of Birth</th>
                    <td>{formatDate(user.dob) || "Not provided"}</td>
                  </tr>
                  <tr>
                    <th>Age</th>
                    <td>{user.age || "Not provided"}</td>
                  </tr>
                  <tr>
                    <th>Marital Status</th>
                    <td>{user.maritalStatus || "Not provided"}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Professional Details Card (New - Optional) */}
          <div className="card mb-4">
            <div className="card-header">
              <h4>Professional Details (Optional)</h4>
            </div>
            <div className="card-body">
              <table className="table">
                <tbody>
                  <tr>
                    <th>Total Experience</th>
                    <td>
                      {user.totalExperience
                        ? `${user.totalExperience} years`
                        : "Not provided"}
                    </td>
                  </tr>
                  <tr>
                    <th>Current CTC</th>
                    <td>
                      {user.currentCTC ? `₹${user.currentCTC}` : "Not provided"}
                    </td>
                  </tr>
                  <tr>
                    <th>Expected CTC</th>
                    <td>
                      {user.expectedCTC
                        ? `₹${user.expectedCTC}`
                        : "Not provided"}
                    </td>
                  </tr>
                  <tr>
                    <th>Notice Period</th>
                    <td>
                      {user.noticePeriod
                        ? `${user.noticePeriod} days`
                        : "Not provided"}
                    </td>
                  </tr>
                  <tr>
                    <th>Willing to Work in Shifts</th>
                    <td>{user.willingToWorkShifts || "Not provided"}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Family Information Card */}
          <div className="card mb-4">
            <div className="card-header">
              <h4>Family Information (Optional)</h4>
            </div>
            <div className="card-body">
              <div className="mb-4">
                <h5>Father</h5>
                {user.family?.father?.name ? (
                  <table className="table">
                    <tbody>
                      <tr>
                        <th>Name</th>
                        <td>{user.family.father.name}</td>
                      </tr>
                      <tr>
                        <th>Occupation</th>
                        <td>{user.family.father.occupation}</td>
                      </tr>
                      <tr>
                        <th>Contact</th>
                        <td>{user.family.father.contact}</td>
                      </tr>
                    </tbody>
                  </table>
                ) : (
                  <p>No information provided</p>
                )}
              </div>

              <div className="mb-4">
                <h5>Mother</h5>
                {user.family?.mother?.name ? (
                  <table className="table">
                    <tbody>
                      <tr>
                        <th>Name</th>
                        <td>{user.family.mother.name}</td>
                      </tr>
                      <tr>
                        <th>Occupation</th>
                        <td>{user.family.mother.occupation}</td>
                      </tr>
                      <tr>
                        <th>Contact</th>
                        <td>{user.family.mother.contact}</td>
                      </tr>
                    </tbody>
                  </table>
                ) : (
                  <p>No information provided</p>
                )}
              </div>

              <div className="mb-4">
                <h5>Spouse</h5>
                {user.family?.spouse?.name ? (
                  <table className="table">
                    <tbody>
                      <tr>
                        <th>Name</th>
                        <td>{user.family.spouse.name}</td>
                      </tr>
                      <tr>
                        <th>Occupation</th>
                        <td>{user.family.spouse.occupation}</td>
                      </tr>
                      <tr>
                        <th>Contact</th>
                        <td>{user.family.spouse.contact}</td>
                      </tr>
                    </tbody>
                  </table>
                ) : (
                  <p>No information provided</p>
                )}
              </div>

              <div>
                <h5>Siblings</h5>
                {user.family?.siblings?.length > 0 ? (
                  user.family.siblings.map((sibling, index) => (
                    <div key={index} className="mb-3">
                      <h6>Sibling {index + 1}</h6>
                      <table className="table">
                        <tbody>
                          <tr>
                            <th>Name</th>
                            <td>{sibling.name}</td>
                          </tr>
                          <tr>
                            <th>Occupation</th>
                            <td>{sibling.occupation}</td>
                          </tr>
                          <tr>
                            <th>Contact</th>
                            <td>{sibling.contact}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  ))
                ) : (
                  <p>No siblings information provided</p>
                )}
              </div>
            </div>
          </div>

          {/* Education Card */}
          <div className="card mb-4">
            <div className="card-header">
              <h4>Education</h4>
            </div>
            <div className="card-body">
              {user.education?.length > 0 ? (
                user.education.map((edu, index) => (
                  <div key={index} className="mb-4">
                    <h5>
                      {edu.exam} - {edu.institution}
                    </h5>
                    <table className="table">
                      <tbody>
                        <tr>
                          <th>Stream/Specialization</th>
                          <td>{edu.stream}</td>
                        </tr>
                        <tr>
                          <th>Percentage/Grade</th>
                          <td>{edu.percentage}</td>
                        </tr>
                        <tr>
                          <th>Passing Year</th>
                          <td>{edu.passingYear}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ))
              ) : (
                <p className="text-danger">No education details available</p>
              )}
            </div>
          </div>

          {/* Work Experience Card */}
          <div className="card mb-4">
            <div className="card-header">
              <h4>Work Experience</h4>
            </div>
            <div className="card-body">
              {user.workExperience?.length > 0 ? (
                user.workExperience.map((exp, index) => (
                  <div key={index} className="mb-4">
                    <h5>
                      {exp.designation} at {exp.employer}
                    </h5>
                    <table className="table">
                      <tbody>
                        <tr>
                          <th>Date of Joining</th>
                          <td>{formatDate(exp.doj)}</td>
                        </tr>
                        <tr>
                          <th>Date of Leaving</th>
                          <td>{formatDate(exp.dol)}</td>
                        </tr>
                        <tr>
                          <th>Tenure</th>
                          <td>{exp.tenure}</td>
                        </tr>
                        <tr>
                          <th>Salary</th>
                          <td>{exp.salary}</td>
                        </tr>
                        <tr>
                          <th>Reason for Leaving</th>
                          <td>{exp.reasonForLeaving}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ))
              ) : (
                <p className="text-danger">
                  No work experience details available
                </p>
              )}
            </div>
          </div>

          {/* References Card - Optional */}
          <div className="card">
            <div className="card-header">
              <h4>References (Optional)</h4>
            </div>
            <div className="card-body">
              {user.references?.length > 0 ? (
                user.references.map((ref, index) => (
                  <div key={index} className="mb-4">
                    <h5>Reference {index + 1}</h5>
                    <table className="table">
                      <tbody>
                        <tr>
                          <th>Name</th>
                          <td>{ref.name}</td>
                        </tr>
                        <tr>
                          <th>Position</th>
                          <td>{ref.position}</td>
                        </tr>
                        <tr>
                          <th>Organization</th>
                          <td>{ref.organization}</td>
                        </tr>
                        <tr>
                          <th>Mobile Number</th>
                          <td>{ref.mobile}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ))
              ) : (
                <p>No references provided</p>
              )}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Employee;
